import { FC, useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router'
import { Breadcrumbs, Button, Input } from '@mondra/ui-components'
import { useQuery } from '@apollo/client'
import { IResources } from './types'
import { getResourcesVars } from './helpers'
import { filterEnums, getTableData } from '../../../shared/gridFunctions'
import Table from '../../../components/Table'
import { GET_ALL_RESOURCES } from '../../../query/resources-sources'
import { ExportGraphqlButton } from '../../../components/ExportGraphqlButton'
import { EXPORT_RESOURCES_SOURCES_QUERY } from '../../../query/export-resources-sources'
import { EXPORT_MAX_ROW_COUNT } from '../../../const'

const defaultColumnDefination = [
  {
    field: 'combinedName',
    headerName: 'Resource-Source Name',
    width: 250,
  },
]
const ResourcesSources: FC = () => {
  const navigate = useNavigate()

  const [tableData, setTableData] = useState<IResources[]>([])
  const [columnDefs, setColumn] = useState([])
  const [tableSearch, setTableSearch] = useState<string | null>(null)
  const [debouncedTableSearch, setDebouncedTableSearch] = useState(tableSearch)
  const [totalCount, setTotalCount] = useState<string>('')
  const [endCursor, setEndCursor] = useState<string | null>(null)
  const [startCursor, setStartCursor] = useState<string | null>(null)
  const [resourcesVars, setResourcesVars] = useState<any>(getResourcesVars)
  const [order, setOrder] = useState([{ id: 'ASC' }])

  const { data, loading, error, refetch } = useQuery(GET_ALL_RESOURCES, {
    variables: {
      str: '',
      first: 100,
      last: null,
      order: order,
      ...getResourcesVars,
    },
  })

  const anyRefetch = (variables?: any): Promise<any> => {
    return refetch(variables)
  }

  const setCursors = () => {
    setEndCursor(data.resourcesSourcesImpacts?.pageInfo?.endCursor)
    setStartCursor(data.resourcesSourcesImpacts?.pageInfo?.startCursor)
  }

  useEffect(() => {
    const timer = setTimeout(() => setTableSearch(debouncedTableSearch), 1000)
    return () => clearTimeout(timer)
  }, [debouncedTableSearch])

  useEffect(() => {
    refetch()
    getTableData(
      data,
      loading,
      setTableData,
      setColumn,
      setTotalCount,
      setCursors,
      '/resource-country-impacts/resource',
      'resourcesSourcesImpacts',
      'resourceSourceImpact',
      defaultColumnDefination
    )
  }, [data])

  useEffect(() => {
    if (tableSearch != null || !order[0].id) {
      if (!loading) {
        setResourcesVars({
          ...filterEnums(tableSearch, getResourcesVars, data),
        })
        anyRefetch({
          str: tableSearch || '',
          ...filterEnums(tableSearch, getResourcesVars, data),
          first: 100,
          after: null,
          before: null,
          last: null,
          order: order,
        })
      }
    }
  }, [tableSearch, order, loading])

  const exportPayload = useMemo(
    () => ({
      operationName: 'ResourcesSourcesImpacts',
      query: EXPORT_RESOURCES_SOURCES_QUERY.loc?.source.body,
      variables: {
        first: EXPORT_MAX_ROW_COUNT,
        after: null,
        where: null,
        order,
      },
    }),
    [order]
  )

  return (
    <div className={'packaging'}>
      <div style={{ maxWidth: '50rem' }} className={'mb-4'}>
        <p className={'mt-3 decoration-gray-600'}>
          <Breadcrumbs
            routes={[
              {
                path: '/resource-country-impacts',
                title: 'Resource Country Impacts',
              },
            ]}
          />
        </p>
        <h2 className={'mb-5 mt-3 text-2xl'}>Resource Country Impacts</h2>
        <div className="packaging-actions items-center">
          <Input
            onChange={e => setDebouncedTableSearch(e.target.value)}
            iconType="search"
            placeholder="Search"
          />
          <Button onClick={() => navigate('/resource-country-impacts/resource?itemId=new')}>
            Add Resource
          </Button>
          <ExportGraphqlButton payload={exportPayload} />
        </div>
      </div>
      <Table
        loading={loading}
        tableData={tableData}
        columnDefs={columnDefs}
        totalNumber={totalCount}
        endCursor={endCursor}
        startCursor={startCursor}
        refetch={refetch}
        defaultVars={resourcesVars || getResourcesVars}
        setCursors={setCursors}
        tableSearch={tableSearch}
        setOrder={setOrder}
        order={order}
        suppressRowClickSelection
      />
    </div>
  )
}

export default ResourcesSources
